import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import IconButton from "@mui/material/IconButton";
import DeleteForever from "@mui/icons-material/DeleteForever";
import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import { Link } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import FileIcon from "assets/images/upstyled/file_icon.jpeg";
import PDFIcon from "assets/images/upstyled/pdf_icon.jpeg";
import { canResizeImageUrl, getFileImage } from "utils/imageHelper";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: "white",
    boxShadow: "none",
    border: "none",
    fontSize: 11,
  },
}));

const thumbsContainer = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 8,
};

const thumbInner = {
  display: "flex",
  minWidth: 0,
  width: "100%",
  overflow: "hidden",
};

function FilePreviewCard({
  files = [],
  setfiles = null,
  handleDelete,
  onFileClick,
  iconOnly = false,
  fileCardStyle = {},
  isDragDisabled = false,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const thumb = {
    display: "inline-flex",
    borderRadius: 4,
    border: "1px solid #eaeaea",
    marginBottom: 2,
    marginRight: 8,
    width: 80,
    height: 80,
    minWidth: 80,
    padding: 4,
    boxSizing: "border-box",
    ...fileCardStyle,
  };

  const handleDrop = (droppedItem) => {
    if (setfiles) {
      //console.log('droppedItem', droppedItem)
      // Ignore drop outside droppable container
      if (!droppedItem.destination) return;
      var updatedList = [...files];
      // Remove dragged item
      const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
      // Add dropped item
      updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
      // Update State
      //console.log('files', files)
      //console.log('updatedList', updatedList)
      setfiles(updatedList);
    }
  };

  const previewComponent = (file) => {
    if (file?.type?.match("image/*") || file?.type == "application/pdf") {
      return (
        <LightTooltip
          placement="top"
          title={
            <SuiBox sx={{ backgroundColor: "transparent", borderRadius: "0px" }}>
              <ImageCardWrapper
                data={file}
                mode={"normal"}
                sx={{ maxWidth: 400, maxHeight: 400 }}
                isOriginalSize={true}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              />
            </SuiBox>
          }
        >
          <ImageCardWrapper
            data={file}
            mode={"normal"}
            width={thumb.width - 4}
            height={thumb.height - 4}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onFileClick ? onFileClick(file) : window.open(file.preview || file.url);
            }}
          />
        </LightTooltip>
      );
    } else {
      return (
        <ImageCardWrapper
          data={file}
          mode={"normal"}
          width={thumb.width - 4}
          height={thumb.height - 4}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onFileClick ? onFileClick(file) : window.open(file.preview || file.url);
          }}
        />
      );
    }
  };
  const onError = (e) => {
    console.log(e, "error in file-viewer");
  };

  return (
    <aside style={thumbsContainer}>
      <DragDropContext onDragEnd={handleDrop}>
        <Droppable droppableId="list-container" direction="horizontal">
          {(provided) => (
            <div
              style={{ display: "flex", overflow: "auto", flexWrap: "wrap" }}
              {...provided.droppableProps}
              ref={provided.innerRef}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {files.map((file, index) => (
                <Draggable
                  key={file.name}
                  draggableId={file.name}
                  index={index}
                  isDragDisabled={isDragDisabled}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.dragHandleProps}
                      {...provided.draggableProps}
                    >
                      <div style={{ float: "right", padding: 2 }}>
                        <div
                          style={thumb}
                          key={file.name}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onMouseLeave={() => setIsHovered(false)}
                          onMouseEnter={() => setIsHovered(true)}
                        >
                          <div style={thumbInner}>{previewComponent(file)}</div>
                          {handleDelete && isHovered ? (
                            <div style={{ position: "relative" }}>
                              <Tooltip title={"Delete the file"} placement="top">
                                <CancelIcon
                                  sx={{
                                    position: "absolute",
                                    right: -8,
                                    top: -8,
                                    cursor: "pointer",
                                    width: 22,
                                    height: 22,
                                  }}
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(file.path ? file.path : file);
                                  }}
                                />
                              </Tooltip>
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>
                        {iconOnly ? null : (
                          <SuiBox mb={1} mr={2}>
                            <Tooltip title={file.path || file.name} placement="top">
                              <SuiBox>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    window.open(file.preview || file.url);
                                  }}
                                  style={{
                                    textAlign: "center",
                                    fontSize: 12,
                                    lineHeight: 1.2,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    width: thumb.width - 4,
                                    minHeight: "20px",
                                    WebkitLineClamp: "2",
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {file.name}
                                </a>
                              </SuiBox>
                            </Tooltip>
                          </SuiBox>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </aside>
  );
}
export default FilePreviewCard;
